import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { GcdmCustomerSearchMatch, GcdmCustomerService } from '../../../customer/clients/gcdm/gcdm-customer.service';
import { RetailCrmService } from '../../../customer/clients/retail-crm/retail-crm.service';
import { ExtendedCustomer } from '../../../customer/model/extended-customer.model';
import { FoundCustomer } from '../../../customer/model/found-customer.model';
import { GcdmSyncResult, GcdmSyncService } from '../../../customer/services/gcdm-sync.service';
import { TaxNumberValidationService } from '../../../customer/services/tax-number-validation.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { VerifyChoiceModalWrapper } from '../../../modal/verify-choice-modal/verify-choice-modal.wrapper';
import { deepEqual } from '../../../utils/objectComparison';
import { usePagination } from '../../../utils/pagination/pagination.provider';
import { RequestWarning, RequestWarningType } from '../../../utils/requests/RequestWarning';
import { CustomerComparisonComponent } from '../customer-comparison/customer-comparison.component';
import { CustomerComparisonRowContainer } from '../customer-comparison-row/customer-comparison-row.container';
import { EqualCustomerSyncComponent } from '../equal-customer-sync/equal-customer-sync.component';
import { NscCustomerSearchComponent } from '../nsc-customer-search/nsc-customer-search.component';

@Component({
  selector: 'cdf-sync-with-nsc-container',
  imports: [
    NscCustomerSearchComponent,
    CustomerComparisonComponent,
    EqualCustomerSyncComponent,
    VerifyChoiceModalWrapper,
  ],
  template: `
    @switch (pagination.currentName()) {
      @case ('customerSearch') {
        <cdf-nsc-customer-search
          [(email)]="email"
          [foundCustomer]="foundCustomers()"
          [isLoading]="isLoading()"
          [searchFailed]="searchFailed()"
          [tooManyResults]="tooManyResults()"
          (searchNscCustomer)="searchNscCustomer()"
          (selectCustomer)="selectCustomer($event)"
          (createNewNscCustomer)="askForCreateConsentModalOpen.set(true)"
        />
      }

      @case ('customerComparison') {
        @if (areCustomersEqual()) {
          <cdf-equal-customer-sync
            [isLoading]="isLoading()"
            (sync)="syncEqualCustomers()"
            (back)="pagination.goTo('customerSearch')"
          />
        } @else {
          <cdf-customer-comparison
            [retailCustomer]="retailCustomer()"
            [nscCustomer]="nscCustomer()"
            [isLoading]="isLoading()"
            (sync)="sync($event)"
            (back)="pagination.goTo('customerSearch')"
          />
        }
      }
    }

    <cdf-verify-choice-modal-wrapper
      [(open)]="askForCreateConsentModalOpen"
      (verify)="createNewNscCustomer()"
      [headingText]="i18n('nscSync.verifyModalHeading')"
      [bodyText]="i18n('nscSync.verifyModalBody')"
      [verifyButtonText]="i18n('nscSync.createNewNscCustomer')"
      [buttonLoading]="askForCreateConsentButtonLoading()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncWithNscContainer implements OnInit {
  protected readonly i18n = useI18n();

  protected readonly dsToastService = inject(DsToastService);
  protected readonly logger = inject(NGXLogger);
  protected readonly gcdmCustomerService = inject(GcdmCustomerService);
  protected readonly gcdmSyncService = inject(GcdmSyncService);
  protected readonly retailCrmService = inject(RetailCrmService);
  protected readonly taxNumberValidationService = inject(TaxNumberValidationService);

  protected readonly pagination = usePagination(['customerSearch', 'customerComparison'], () => {});

  /**
   * Account Number of customer whose profile was used to open the modal
   */
  readonly accountNumber = input.required<string>();
  readonly initialEmail = input<string>();
  readonly closeEvent = output();
  readonly reloadPage = output();

  // NSC Database customer search
  protected readonly email = signal('');
  protected readonly searchResult = signal<
    { searchedEmailAddress: string; matches: GcdmCustomerSearchMatch[] } | undefined
  >(undefined);
  protected readonly foundCustomers = computed(() => this.searchResult()?.matches.map((match) => match.foundCustomer));
  protected readonly searchFailed = signal(false);
  protected readonly tooManyResults = signal(false);

  // Selected customer for merging
  protected readonly nscCustomer = signal<ExtendedCustomer>({});
  protected readonly retailCustomer = signal<ExtendedCustomer>({});

  protected readonly askForCreateConsentModalOpen = signal(false);
  protected readonly askForCreateConsentButtonLoading = signal(false);

  /**
   * GCID of a customer that was created in the NSC Database but could not be linked to
   * Retail CRM. A retry must reuse it: calling the create path again would produce a second
   * business partner for the same person (see {@link GcdmSyncResult}).
   */
  private readonly pendingGcid = signal<string | undefined>(undefined);

  readonly isLoading = model<boolean>(false);

  private toastId = 'SYNC_WITH_NSC_CONTAINER_TOAST';

  protected readonly areCustomersEqual = computed(() => {
    const customerKeys: (keyof ExtendedCustomer)[] = Object.values(
      CustomerComparisonRowContainer.extendedCustomerFieldsMap,
    ).filter((value) => value !== 'accountNumber' && value !== 'gcid');

    return customerKeys.every((fieldName) =>
      deepEqual(this.retailCustomer()[fieldName], this.nscCustomer()[fieldName]),
    );
  });

  ngOnInit() {
    const initialEmail = this.initialEmail();
    if (initialEmail) {
      this.email.set(initialEmail);
      void this.searchNscCustomer();
    }
  }

  protected async selectCustomer(selectedNscCustomer: FoundCustomer) {
    this.dsToastService.popToast(this.toastId);
    this.isLoading.set(true);

    const gcid = selectedNscCustomer.gcid;
    if (!gcid) {
      this.logger.error('#CDFTF0128 Could not select Customer as no GCID was present.');
      this.showSelectCustomerFailure();
      this.isLoading.set(false);
      return;
    }

    // The business partner was already fetched by the search, so unlike the ESO CRM flow no
    // second "load customer by GCID" request is needed (CUSTAPP-611).
    const match = this.searchResult()?.matches.find((candidate) => candidate.foundCustomer.gcid === gcid);
    if (!match) {
      this.logger.error(`#CDFTF0126 Could not find customer for GCID ${gcid}`);
      this.showSelectCustomerFailure();
      this.isLoading.set(false);
      return;
    }

    let retailCustomer: ExtendedCustomer;
    try {
      retailCustomer = await this.retailCrmService.getAccountForComparisonByAccountNumber(this.accountNumber());
    } catch (e) {
      this.logger.error('#CDFTF0127 Could not fetch customer data', e);
      this.showSelectCustomerFailure();
      return;
    } finally {
      this.isLoading.set(false);
    }

    this.retailCustomer.set(retailCustomer);
    this.nscCustomer.set({
      ...match.extendedCustomer,
      // `description` is Retail CRM-only: RT CRM's GCDM mapping has no counterpart in either
      // direction. Showing it as empty would let the user clear it without noticing, since the
      // comparison defaults to the NSC value and CDF displays the field nowhere else.
      description: retailCustomer.description,
    });
    this.pagination.next();
  }

  /**
   * Case B1: the customer has no NSC Database record yet, so it is created and then linked.
   * If a previous attempt already created the NSC Database record and only the Retail CRM link
   * failed, this retries the link against the existing GCID instead of creating a duplicate.
   */
  protected async createNewNscCustomer() {
    this.dsToastService.popToast(this.toastId);
    this.askForCreateConsentButtonLoading.set(true);
    try {
      const accountNumber = this.accountNumber();
      const retailCustomer = await this.retailCrmService.getAccountForComparisonByAccountNumber(accountNumber);
      if (
        !this.taxNumberValidationService.isTaxNumberValid(retailCustomer.taxNumber, retailCustomer.taxNumberCountry)
      ) {
        this.logger.error('#CDFTF0279 Unable to create the NSC Database customer as the tax number is invalid.');
        this.showInvalidTaxNumberFailure();
        return;
      }
      const pendingGcid = this.pendingGcid();

      const result = pendingGcid
        ? await this.gcdmSyncService.updateAndLink(accountNumber, pendingGcid, {
            ...retailCustomer,
            gcid: pendingGcid,
          })
        : await this.gcdmSyncService.syncNewGcdmCustomer(accountNumber, retailCustomer);

      if (result.status === 'RETAIL_LINK_FAILED') {
        // Remember the GCID so the next attempt links instead of creating again.
        this.pendingGcid.set(result.gcid);
      }

      if (!this.handleSyncResult(result)) {
        return;
      }

      this.pendingGcid.set(undefined);
      this.askForCreateConsentModalOpen.set(false);
      this.finishSync();
    } catch (e) {
      this.logger.error('#CDFTF0146 Could not create new customer in the NSC Database', e);
      this.showSyncFailure(e);
    } finally {
      this.askForCreateConsentButtonLoading.set(false);
    }
  }

  protected async syncEqualCustomers() {
    const mergedCustomer: ExtendedCustomer = { ...this.retailCustomer(), gcid: this.nscCustomer().gcid };
    await this.sync(mergedCustomer);
  }

  /** CUSTAPP-611 - Case B2: an existing NSC Database record is updated with the merged data and then linked. */
  protected async sync(combinedCustomer: ExtendedCustomer) {
    this.dsToastService.popToast(this.toastId);

    const { accountNumber, gcid, emailPrivate, taxNumber, taxNumberCountry } = combinedCustomer;

    // Validated before any request is started, so these never reach the catch below.
    if (!accountNumber) {
      this.logger.error(
        "#CDFTF0137 Unable to sync to the NSC Database as required field 'accountNumber' is undefined.",
      );
      this.showSyncFailure();
      return;
    }
    if (!gcid) {
      this.logger.error("#CDFTF0138 Unable to sync to the NSC Database as required field 'gcid' is undefined.");
      this.showSyncFailure();
      return;
    }
    if (!emailPrivate) {
      this.logger.error("#CDFTF0277 Unable to sync to the NSC Database as required field 'emailPrivate' is undefined.");
      this.showSyncFailure();
      return;
    }
    if (!this.taxNumberValidationService.isTaxNumberValid(taxNumber, taxNumberCountry)) {
      this.logger.error(
        `#CDFTF0279 Unable to sync to the NSC Database as the tax number is invalid for country ${taxNumberCountry}.`,
      );
      this.showInvalidTaxNumberFailure();
      return;
    }

    this.isLoading.set(true);
    try {
      if (await this.isMainEmailAlreadySynced(emailPrivate, gcid, accountNumber)) {
        this.showEmailAlreadySyncedFailure();
        return;
      }

      const result = await this.gcdmSyncService.updateAndLink(accountNumber, gcid, combinedCustomer);

      if (!this.handleSyncResult(result)) {
        return;
      }

      this.finishSync();
    } catch (e) {
      this.logger.error('#CDFTF0124 Could not synchronize Retail CRM customer with NSC Database customer', e);
      this.showSyncFailure(e);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * @returns true if the sync completed, false if only the NSC Database write succeeded - the
   *          modal then stays open so the user can retry the link without creating a duplicate.
   */
  private handleSyncResult(result: GcdmSyncResult): boolean {
    if (result.status === 'RETAIL_LINK_FAILED') {
      this.showRetailLinkFailure();
      // The customer now exists in the NSC Database, which the profile page should reflect.
      this.reloadPage.emit();
      return false;
    }
    return true;
  }

  private finishSync() {
    this.showSyncSuccess();
    this.reloadPage.emit();
    this.closeEvent.emit();
  }

  /*
   * This works by searching for an existing account with this email address in the NSC Database.
   * If any such accounts are found, we check Retail CRM for *another* customer that is linked to it (by GCID).
   * If any such customers are found, the sync is blocked: one NSC Database customer must not be
   * linked to two Retail CRM accounts.
   *
   * The account being synced itself is not a duplicate: after its C2D was terminated it is no longer
   * in sync but still holds its GCID, and must be allowed to re-sync (CUSTAPP-783).
   *
   * The NSC Database deduplicates on its own when creating a customer, but not when linking an
   * existing one, so this guard is still required for case B2.
   */
  private async isMainEmailAlreadySynced(mergedEmailPrivate: string, gcid: string, accountNumber: string) {
    let lastSearchResults = this.searchResult();

    // fitting search already cached or need to be renewed?
    if (lastSearchResults?.searchedEmailAddress !== mergedEmailPrivate) {
      this.email.set(mergedEmailPrivate);
      await this.searchNscCustomer();
      lastSearchResults = this.searchResult();
    }

    const foundCustomersWithEmail = (lastSearchResults?.matches ?? [])
      .map((match) => match.foundCustomer)
      .filter((customer) => customer.email === mergedEmailPrivate);

    if (foundCustomersWithEmail.length > 0) {
      const mailExistsInAnotherNscCustomer = foundCustomersWithEmail.some((customer) => customer.gcid !== gcid);
      if (mailExistsInAnotherNscCustomer) {
        this.logger.error(
          '#CDFFF0200 Prevent sync of a Retail customer because there is already another customer with this main email address in the NSC Database.',
        );
        return true;
      }

      const resultsInRetail = await Promise.all(
        foundCustomersWithEmail.map((customer) => this.retailCrmService.searchAccounts({ gcid: customer.gcid })),
      );
      // An account without an account number cannot be told apart from the current one, so it
      // counts as "another" account and still blocks the sync.
      const anotherRetailAccountAlreadyLinked = resultsInRetail
        .flat()
        .some((retailAccount) => retailAccount.accountNumber !== accountNumber);
      if (anotherRetailAccountAlreadyLinked) {
        this.logger.error(
          '#CDFFF0200 Prevent sync of a Retail customer because another Retail customer is already linked to an NSC Database account using this main email address.',
        );
        return true;
      }
    }
    return false;
  }

  protected async searchNscCustomer() {
    const email = this.email();
    try {
      this.isLoading.set(true);
      this.searchFailed.set(false);
      this.tooManyResults.set(false);

      const matches = await this.gcdmCustomerService.searchCustomersForSync({ email });
      this.searchResult.set({ searchedEmailAddress: email, matches });
    } catch (e) {
      if (e instanceof RequestWarning && e.type === RequestWarningType.TOO_MANY_RESULTS) {
        // Not a failure: the search was too broad to be answered.
        this.logger.info('#CDFTF0276 The NSC Database returned too many results for the sync search.', e);
        this.tooManyResults.set(true);
      } else {
        this.logger.error('#CDFTF0125 Failed searching for customers in the NSC Database.', e);
        this.searchFailed.set(true);
      }
      this.searchResult.set(undefined);
    } finally {
      this.isLoading.set(false);
    }
  }

  private showSyncFailure(error?: unknown) {
    const concurrentUpdate = error instanceof RequestWarning && error.type === RequestWarningType.CONCURRENT_UPDATE;

    this.dsToastService.pushToast({
      toastTitle: this.i18n('nscSync.syncFailureErrorTitle'),
      toastText: concurrentUpdate
        ? this.i18n('nscSync.syncFailureConcurrentUpdateMessage')
        : this.i18n('nscSync.syncFailureErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showRetailLinkFailure() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('nscSync.retailLinkFailureTitle'),
      toastText: this.i18n('nscSync.retailLinkFailureMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showEmailAlreadySyncedFailure() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('nscSync.syncFailureErrorTitle'),
      toastText: this.i18n('nscSync.syncFailureEmailDuplicationErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showInvalidTaxNumberFailure() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('nscSync.syncFailureErrorTitle'),
      toastText: this.i18n('nscSync.syncFailureInvalidTaxNumberMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }

  private showSyncSuccess() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('nscSync.syncSuccessTitle'),
      toastText: this.i18n('nscSync.syncSuccessMessage'),
      id: this.toastId,
      tone: 'positive',
    });
  }

  private showSelectCustomerFailure() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('nscSync.selectCustomerErrorTitle'),
      toastText: this.i18n('nscSync.selectCustomerErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }
}
